/**
 * Geocodificación vía Nominatim (OpenStreetMap).
 * Uso aceptable: ~1 petición/seg; User-Agent identificativo obligatorio.
 * https://operations.osmfoundation.org/policies/nominatim/
 */

const NOMINATIM_UA = 'DreamVinyl/1.0 (marketplace; https://dreamvinyl.es)';

/**
 * @param {string} municipio
 * @param {string} [provincia]
 * @returns {Promise<{ lat: number|null, lng: number|null }>}
 */
export async function geocodeMunicipio(municipio, provincia) {
  const mun = String(municipio ?? '').trim();
  if (!mun) return { lat: null, lng: null };

  try {
    const prov = String(provincia ?? '').trim();
    const qRaw = prov ? `${mun}, ${prov}, España` : `${mun}, España`;
    const q = encodeURIComponent(qRaw);
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=es`,
      { headers: { 'User-Agent': NOMINATIM_UA } }
    );
    if (!res.ok) return { lat: null, lng: null };
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) return { lat: null, lng: null };
    const lat = parseFloat(data[0].lat);
    const lng = parseFloat(data[0].lon);
    return {
      lat: Number.isFinite(lat) ? lat : null,
      lng: Number.isFinite(lng) ? lng : null,
    };
  } catch (_) {
    return { lat: null, lng: null };
  }
}

function isSpainPais(pais) {
  const s = String(pais ?? '')
    .trim()
    .toLowerCase();
  return s === 'españa' || s === 'spain' || s === 'es';
}

/**
 * Coordenadas para un anuncio con modelo INE España (`ciudad` = provincia, `localidad` = municipio).
 * @param {{ pais?: string|null, ciudad?: string|null, localidad?: string|null }} ubicacion
 */
export async function geocodeListingUbicacion(ubicacion) {
  if (!ubicacion || !isSpainPais(ubicacion.pais)) return { lat: null, lng: null };
  const prov = String(ubicacion.ciudad ?? '').trim();
  const mun = String(ubicacion.localidad ?? '').trim();
  if (mun) return geocodeMunicipio(mun, prov);
  if (prov) return geocodeMunicipio(prov, '');
  return { lat: null, lng: null };
}

/** Distancia en km (Haversine). */
export function distanciaKm(lat1, lng1, lat2, lng2) {
  const a1 = Number(lat1);
  const o1 = Number(lng1);
  const a2 = Number(lat2);
  const o2 = Number(lng2);
  if (![a1, o1, a2, o2].every(Number.isFinite)) return Infinity;
  const R = 6371;
  const dLat = ((a2 - a1) * Math.PI) / 180;
  const dLng = ((o2 - o1) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a1 * Math.PI) / 180) * Math.cos((a2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}
